import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, MapPin } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { siteConfig, fullAddress, isPlaceholder } from "../../config/site";
import { Reveal } from "../Reveal";
import { IconChip } from "../IconChip";

export const MapPlaceholder = ({ className = "" }: { className?: string }) => {
  const { t } = useLanguage();
  const maps = siteConfig.bookingLinks.googleMaps;
  const embed = siteConfig.bookingLinks.googleMapsEmbed;
  if (embed) {
    return <iframe title="Google Maps" src={embed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className={`w-full rounded-3xl border-0 ${className}`} data-testid="map-embed" />;
  }
  return (
    <div className={`grain relative flex flex-col items-center justify-center overflow-hidden rounded-3xl bg-sand-100 text-center ${className}`} data-testid="map-placeholder">
      <div aria-hidden className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(#18181b_1px,transparent_1px),linear-gradient(90deg,#18181b_1px,transparent_1px)] [background-size:32px_32px]" />
      <span className="relative"><IconChip icon={MapPin} size="lg" /></span>
      <p className="relative mt-4 font-display text-lg">{fullAddress()}</p>
      <p className="relative mt-1 max-w-xs text-sm text-charcoal-500">{isPlaceholder(maps) ? t.location.mapPlaceholder : ""}</p>
      {!isPlaceholder(maps) && <a href={maps} target="_blank" rel="noopener noreferrer" className="btn-dark relative mt-4" data-testid="map-open-link"><ExternalLink className="h-4 w-4" aria-hidden /> {t.location.openMaps}</a>}
    </div>
  );
};

export const LocationSection = () => {
  const { t } = useLanguage();
  return (
    <section className="section bg-sand-50/60" data-testid="location-section">
      <div className="container-x grid gap-12 lg:grid-cols-12 lg:items-center">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow">{t.location.eyebrow}</p>
          <h2 className="h2 mt-4">{t.location.title}</h2>
          <p className="lede mt-6">{t.location.text}</p>
          <address className="mt-8 not-italic font-display text-xl" data-testid="location-address">{fullAddress()}</address>
          <ul className="mt-6 flex flex-wrap gap-2">
            {t.location.tags.map((tag) => <li key={tag} className="rounded-full border border-charcoal/15 px-3.5 py-1.5 text-xs font-medium">{tag}</li>)}
          </ul>
          <Link to="/lage" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-clay hover:gap-3 transition-[gap] duration-200" data-testid="location-more-link">{t.location.morePage} <ArrowRight className="h-4 w-4" aria-hidden /></Link>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.15}>
          <MapPlaceholder className="aspect-[4/3] lg:aspect-[16/11]" />
        </Reveal>
      </div>
    </section>
  );
};
